import './polyfills';
import { installInterceptors, registerPageProvider, registerUnauthorizedHandler } from '@platform/api';
import { createSentryFlagPropagator } from '@platform/observability';
import { createTrackingFlagPropagator, getStickinessKey } from '@platform/tracking';
import '@platform/ui/globals.css';
import '@platform/ui/fonts.css';
import { AuthProvider } from '@hub/auth';
import { login, router } from '@hub/router';
import { FeatureFlagProvider } from '@platform/feature-flags';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { AppErrorBoundary } from './app/AppErrorBoundary';
import { AuthedRouterProvider } from './app/AuthedRouterProvider';
import { AuthenticatedBannerProvider } from './app/banners';
import { bootstrapPreloadErrorReload, bootstrapSentry, bootstrapTracking, errorHandlers } from './app/bootstrap';
import { FeedbackFlagSync } from './app/FeedbackFlagSync';
import { ObservabilitySamplingSync } from './app/ObservabilitySamplingSync';
import { queryClient, registerErrorDeps } from './app/queryClient';
import { WpsRibbonRemover } from './app/WpsRibbonRemover';
import { BookingJourneyProvider } from './journey/BookingJourneyProvider';
import { logger } from './logger';
import { getLocale } from './paraglide/runtime.js';

// Before anything can reach the SDK: session refresh, 401 redirect and ApiError mapping all live in
// the client's interceptors. `baseUrl`/`credentials`/`fetch` are applied at client construction.
installInterceptors();
bootstrapPreloadErrorReload();
bootstrapTracking();
bootstrapSentry(router);
registerErrorDeps({ navigate: router.navigate });
// buildLoginRedirectURL (router/redirect-config.ts) defaults the redirect target to
// the current pathname+search, so we don't pass one explicitly — the no-window-location
// grit plugin restricts that helper to the auth/redirect-config allowlist.
registerUnauthorizedHandler(() => login({ reason: 'session_expired' }));
// The innermost match is the page; outer entries are layouts. Read per request rather
// than on navigation, so a call in flight across a route change reports where it started.
registerPageProvider(() => router.state.matches.at(-1)?.fullPath ?? null);
document.documentElement.lang = getLocale();

// Dev-only BFF mocking: the worker always runs in dev but defaults to pass-through, so this is inert
// until you flip a scenario on in the "BFF Mocks" devtools panel. Interception is session-only — a
// refresh resets to pass-through. Gated on `import.meta.env.DEV` so this branch, `./mocks/browser`, and
// `msw/browser` are tree-shaken from the production bundle. Awaited before render. See src/mocks/.
if (import.meta.env.DEV) {
  const { startMockWorker } = await import('./mocks/browser');
  await startMockWorker().catch(() => {
    logger.debug('msw failed to start');
  });
}

// Unleash calls its stickiness slot sessionId. Every rollout strategy must be set to stickiness
// "sessionId", else the default `userId || sessionId` re-buckets the user at login.
const flagContext = { sessionId: getStickinessKey() };

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement, errorHandlers);

root.render(
  <StrictMode>
    <FeatureFlagProvider
      type="unleash"
      scriptId="unleash-flags"
      logger={logger}
      initialContext={flagContext}
      propagators={[createSentryFlagPropagator(), createTrackingFlagPropagator()]}
    >
      <ObservabilitySamplingSync />
      <FeedbackFlagSync />
      <WpsRibbonRemover />
      <AppErrorBoundary onReset={() => router.navigate({ to: '/' })}>
        {/* Auth-independent: mounted above AuthProvider so public entry landings can
            capture the booking journey just as authenticated steps read it. */}
        <BookingJourneyProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <AuthenticatedBannerProvider>
                <AuthedRouterProvider />
              </AuthenticatedBannerProvider>
            </AuthProvider>
          </QueryClientProvider>
        </BookingJourneyProvider>
      </AppErrorBoundary>
    </FeatureFlagProvider>
  </StrictMode>,
);
